// Log levels
enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
  TRACE = 4
}

interface LogMeta {
  [key: string]: any;
}

interface LoggerConfig {
  enabled: boolean;
  level: keyof typeof LogLevel;
  environment: string;
  persistToLocalStorage: boolean;
  maxStorageEntries: number;
  apiEndpoint?: string; // Optional: send logs to backend
}

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
  meta: LogMeta;
  url: string;
  userAgent: string;
}

class FrontendLogger {
  private config: LoggerConfig;
  private currentLogLevel: LogLevel;
  private storageKey = 'app_logs';

  constructor() {
    this.config = {
      enabled: process.env.REACT_APP_LOGGING_ENABLED !== 'false',
      level: (process.env.REACT_APP_LOG_LEVEL as keyof typeof LogLevel) || 'INFO',
      environment: process.env.NODE_ENV || 'production',
      persistToLocalStorage: process.env.REACT_APP_PERSIST_LOGS === 'false',
      maxStorageEntries: parseInt(process.env.REACT_APP_MAX_LOG_ENTRIES || '1000'),
      apiEndpoint: process.env.REACT_APP_LOG_API_ENDPOINT// || 'http://localhost:5000/api/logs'
    };

    this.currentLogLevel = LogLevel[this.config.level] ?? LogLevel.INFO;

    // Clean up old logs on initialization
    this.cleanupStorage();

    // Set up global error handling
    this.setupGlobalErrorHandling();
  }

  private formatMessage(level: string, message: string, meta: LogMeta = {}): string {
    const timestamp = new Date().toISOString();
    const metaString = Object.keys(meta).length > 0 ? ` | Meta: ${JSON.stringify(meta)}` : '';
    return `[${timestamp}] [${level}] ${message}${metaString}`;
  }

  private createLogEntry(level: string, message: string, meta: LogMeta = {}): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      meta,
      url: window.location.href,
      userAgent: navigator.userAgent
    };
  }

  private persistToStorage(entry: LogEntry): void {
    if (!this.config.persistToLocalStorage) return;

    try {
      const existingLogs = this.getStoredLogs();
      existingLogs.push(entry);

      // Keep only the most recent entries
      if (existingLogs.length > this.config.maxStorageEntries) {
        existingLogs.splice(0, existingLogs.length - this.config.maxStorageEntries);
      }

      localStorage.setItem(this.storageKey, JSON.stringify(existingLogs));
    } catch (error) {
      console.error('Failed to persist log to localStorage:', error);
    }
  }

  private sendToAPI(entry: LogEntry): void {
    if (!this.config.apiEndpoint) return;

    // Use fetch to send log to backend (fire and forget)
    fetch(this.config.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(entry)
    }).catch(error => {
      // Silently fail to avoid infinite logging loops
      console.error('Failed to send log to API:', error);
    });
  }

  private cleanupStorage(): void {
    if (!this.config.persistToLocalStorage) return;

    const logs = this.getStoredLogs();
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    
    const recentLogs = logs.filter(log => new Date(log.timestamp) > oneDayAgo);
    
    if (recentLogs.length !== logs.length) {
      localStorage.setItem(this.storageKey, JSON.stringify(recentLogs));
    }
  }

  private setupGlobalErrorHandling(): void {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.error('Unhandled Promise Rejection', {
        reason: event.reason,
        stack: event.reason?.stack
      });
    });

    // Handle JavaScript errors
    window.addEventListener('error', (event) => {
      this.error('JavaScript Error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack
      });
    });
  }

  private log(level: keyof typeof LogLevel, message: string, meta: LogMeta = {}): void {
    if (!this.config.enabled) return;

    const levelValue = LogLevel[level];
    if (levelValue > this.currentLogLevel) return;

    const formattedMessage = this.formatMessage(level, message, meta);
    const entry = this.createLogEntry(level, message, meta);

    // Console output with styling in development
    if (this.config.environment === 'development') {
      const styles = {
        ERROR: 'color: #ff4444; font-weight: bold;',
        WARN: 'color: #ffaa00; font-weight: bold;',
        INFO: 'color: #0099ff;',
        DEBUG: 'color: #00aa00;',
        TRACE: 'color: #aa00aa;'
      };
      
      console.log(`%c${formattedMessage}`, styles[level] || '');
    } else {
      console.log(formattedMessage);
    }

    // Persist to localStorage if enabled
    this.persistToStorage(entry);

    // Send to API if configured
    this.sendToAPI(entry);
  }

  // Public logging methods
  error(message: string, meta: LogMeta = {}): void {
    this.log('ERROR', message, meta);
  }

  warn(message: string, meta: LogMeta = {}): void {
    this.log('WARN', message, meta);
  }

  info(message: string, meta: LogMeta = {}): void {
    this.log('INFO', message, meta);
  }

  debug(message: string, meta: LogMeta = {}): void {
    this.log('DEBUG', message, meta);
  }

  trace(message: string, meta: LogMeta = {}): void {
    this.log('TRACE', message, meta);
  }

  // Utility methods for specific frontend use cases
  apiRequest(method: string, url: string, statusCode: number, duration: number, meta: LogMeta = {}): void {
    this.info(`API ${method} ${url} - ${statusCode} (${duration}ms)`, meta);
  }

  apiError(method: string, url: string, error: Error, meta: LogMeta = {}): void {
    this.error(`API ${method} ${url} - Error: ${error.message}`, {
      ...meta,
      stack: error.stack,
      name: error.name
    });
  }

  userInteraction(action: string, element: string, meta: LogMeta = {}): void {
    this.debug(`User Interaction: ${action} on ${element}`, meta);
  }

  routeChange(from: string, to: string, meta: LogMeta = {}): void {
    this.info(`Route Change: ${from} -> ${to}`, meta);
  }

  componentError(componentName: string, error: Error, meta: LogMeta = {}): void {
    this.error(`Component Error in ${componentName}: ${error.message}`, {
      ...meta,
      stack: error.stack,
      componentName
    });
  }

  performanceMetric(metric: string, value: number, unit: string = 'ms', meta: LogMeta = {}): void {
    this.debug(`Performance: ${metric} = ${value}${unit}`, meta);
  }

  // Utility methods for log management
  getStoredLogs(): LogEntry[] {
    if (!this.config.persistToLocalStorage) return [];
    
    try {
      const logs = localStorage.getItem(this.storageKey);
      return logs ? JSON.parse(logs) : [];
    } catch (error) {
      console.error('Failed to retrieve logs from localStorage:', error);
      return [];
    }
  }

  clearStoredLogs(): void {
    if (this.config.persistToLocalStorage) {
      localStorage.removeItem(this.storageKey);
      this.info('Cleared stored logs');
    }
  }

  exportLogs(): string {
    const logs = this.getStoredLogs();
    return JSON.stringify(logs, null, 2);
  }

  // Runtime configuration methods
  setLogLevel(level: keyof typeof LogLevel): void {
    if (LogLevel[level] !== undefined) {
      this.currentLogLevel = LogLevel[level];
      this.info(`Log level changed to: ${level}`);
    } else {
      this.warn(`Invalid log level: ${level}`);
    }
  }

  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
    if (enabled) {
      this.info('Logging enabled');
    }
  }

  // Get current configuration
  getConfig(): LoggerConfig {
    return { ...this.config };
  }
}

// Create singleton instance
const logger = new FrontendLogger();

// React Hook for component-level logging
export const useLogger = () => {
  return {
    error: (message: string, meta?: LogMeta) => logger.error(message, meta),
    warn: (message: string, meta?: LogMeta) => logger.warn(message, meta),
    info: (message: string, meta?: LogMeta) => logger.info(message, meta),
    debug: (message: string, meta?: LogMeta) => logger.debug(message, meta),
    trace: (message: string, meta?: LogMeta) => logger.trace(message, meta),
    userInteraction: (action: string, element: string, meta?: LogMeta) => 
      logger.userInteraction(action, element, meta),
    componentError: (componentName: string, error: Error, meta?: LogMeta) => 
      logger.componentError(componentName, error, meta)
  };
};

export default logger;